import { isSignedIn } from "./progressSync";

/*
 * Likes + bookmarks (brief v2.2 §7.1). One GET /user/engagement per
 * session fills a module-level store of liked/bookmarked ids; every
 * button then renders its state from a membership check, and toggles are
 * optimistic (flip locally, POST, revert on failure). Signed-out taps
 * never reach here — the buttons route to sign-in with a return path.
 */

const BASE = process.env.REACT_APP_API || "https://api.insidechassidus.org/api";
const AUTH_RETURN_KEY = "ic-auth-return";

const store = {
  loaded: false,
  liked: { classIds: new Set(), seriesIds: new Set() },
  bookmarked: { classIds: new Set(), seriesIds: new Set() },
};
const listeners = new Set();
let version = 0;

function emit() {
  version += 1;
  listeners.forEach((l) => l());
}

export function subscribeEngagement(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
export function engagementVersion() {
  return version;
}

function headers() {
  const token = localStorage.getItem("accessToken");
  return token
    ? { Accept: "application/json", "Content-Type": "application/json", Authorization: `Bearer ${token}` }
    : null;
}

let loadPromise = null;
export function loadEngagement() {
  if (!isSignedIn()) return Promise.resolve(null);
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const res = await fetch(`${BASE}/user/engagement`, { headers: headers() });
      if (!res.ok) return null;
      const json = await res.json();
      if (!json?.status) return null;
      const toSet = (a) => new Set((a || []).map(Number));
      store.liked = { classIds: toSet(json.data.liked?.class_ids), seriesIds: toSet(json.data.liked?.series_ids) };
      store.bookmarked = {
        classIds: toSet(json.data.bookmarked?.class_ids),
        seriesIds: toSet(json.data.bookmarked?.series_ids),
      };
      store.loaded = true;
      emit();
      return store;
    } catch (_) {
      return null;
    }
  })();
  return loadPromise;
}

function setFor(kind, target) {
  return store[kind][target === "class" ? "classIds" : "seriesIds"];
}

export function isLiked(target, id) {
  return setFor("liked", target).has(Number(id));
}
export function isBookmarked(target, id) {
  return setFor("bookmarked", target).has(Number(id));
}

async function toggle(kind, target, id) {
  const set = setFor(kind, target);
  const n = Number(id);
  const wasActive = set.has(n);
  // Optimistic flip; revert if the request fails.
  if (wasActive) set.delete(n);
  else set.add(n);
  emit();
  try {
    const path = kind === "liked" ? "user/likes/toggle" : "user/bookmarks/toggle";
    const res = await fetch(`${BASE}/${path}`, {
      method: "POST",
      headers: headers(),
      body: JSON.stringify(target === "class" ? { class_id: n } : { series_id: n }),
    });
    const json = res.ok ? await res.json() : null;
    if (!json?.status) throw new Error("toggle failed");
    // Trust the server's final say (double-taps, other devices).
    if (json.data.active) set.add(n);
    else set.delete(n);
    emit();
    return json.data;
  } catch (_) {
    if (wasActive) set.add(n);
    else set.delete(n);
    emit();
    return null;
  }
}

export const toggleLike = (target, id) => toggle("liked", target, id);
export const toggleBookmark = (target, id) => toggle("bookmarked", target, id);

/**
 * Signed-out taps: remember exactly where the user is (path + scroll is
 * handled by resume-on-return of the player, which never unmounts) and
 * send them to sign-in. Sign-in reads and clears this.
 */
export function rememberAuthReturn(path) {
  try {
    sessionStorage.setItem(AUTH_RETURN_KEY, path);
  } catch (_) {}
}
export function takeAuthReturn() {
  try {
    const v = sessionStorage.getItem(AUTH_RETURN_KEY);
    sessionStorage.removeItem(AUTH_RETURN_KEY);
    return v;
  } catch (_) {
    return null;
  }
}

export { isSignedIn };
